/* eslint-disable no-use-before-define */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import TextField from "@mui/material/TextField";
import Autocomplete, {createFilterOptions} from "@mui/material/Autocomplete";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { Checkbox } from "@mui/material";
import { styled } from "@mui/material/styles";
import "../styles/MultiSelect.css";
import { ReactComponent as DownArrowIcon } from "../assets/down-arrow-blue.svg";

const CustomAutocomplete = styled(Autocomplete)({
  '& .MuiAutocomplete-tag': {
    width: 65,
    fontSize: 12,
    height: 13,
    float: 'left'
  },
  '& .MuiChip-deletable': {
    height: 22
  },
  '& .MuiAutocomplete-input': {
    maxWidth: 50
  }
});

const MultiSelect = ({
  items = [],
  label,
  placeholder,
  selectAllLabel,
  noOptionsText,
  limitTags = 2,
  onChange,
  searchedDcValues,
  dcResetKey,
  triggerSearch,
  onPaste
}) => {

  const [selectedOptions, setSelectedOptions] = useState(searchedDcValues);
  const allSelected = items.length === selectedOptions.length;
  const handleToggleOption = newOptions =>
    setSelectedOptions(newOptions);
  const handleClearOptions = () => {
    setSelectedOptions([]);
    onChange([]);
  };
  const itemsPlusAll = [selectAllLabel, ...items];
  const getOptionLabel = option => `${option}`;
  const handleSelectAll = isSelected => {
    if (isSelected) {
      setSelectedOptions(items);
    } else {
      handleClearOptions();
    }
  };

  useEffect(() => {
    setSelectedOptions(searchedDcValues);
  }, [searchedDcValues]);

  const handleToggleSelectAll = () => {
    handleSelectAll && handleSelectAll(!allSelected);
  };

  const handleChange = (event, selectedOptions, reason) => {
    if (event.key === "Enter" && reason === "removeOption") {
      return triggerSearch(event);
    }
    if (reason === "selectOption" || reason === "removeOption") {
      if (selectedOptions.find(option => option === selectAllLabel)) {
        handleToggleSelectAll();
        let result = [];
        result = items.filter(el => el !== selectAllLabel);
        return onChange(result);
      } else {
        handleToggleOption && handleToggleOption(selectedOptions);
        return onChange(selectedOptions);
      }
    }
    /* istanbul ignore next */
    if (reason === "clear") {
      handleClearOptions && handleClearOptions();
    }
  };

  const optionRenderer = (props, option, { selected }) => {
    const selectAllProps =
      option === selectAllLabel
        ? { checked: allSelected }
        : {};
    return (
      <li {...props}>
        <Checkbox
          color="primary"
          size="small"
          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
          checkedIcon={<CheckBoxIcon fontSize="small" />}
          style={{ marginRight: 8 }}
          checked={selected}
          {...selectAllProps}
        />
        {getOptionLabel(option)}
      </li>
    );
  };

  const inputRenderer = params => (
    <TextField
      {...params}
      label={label}
      placeholder={placeholder}
      variant="outlined"
      aria-invalid={false}
      onPaste={onPaste}
    />
  );

  const isOptionEqualToValue = (option, anotherOption) =>
    option === anotherOption;
  const filter = createFilterOptions();
  return (
    <CustomAutocomplete
      id="customAutoComplete"
      multiple
      size="small"
      limitTags={limitTags}
      options={itemsPlusAll}
      value={selectedOptions}
      disableCloseOnSelect
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      noOptionsText={noOptionsText}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);
        return [...filtered];
      }}
      onChange={handleChange}
      renderOption={optionRenderer}
      renderInput={inputRenderer}
      popupIcon={<DownArrowIcon/>}
      autoHighlight={false}
    />
  );
};

MultiSelect.propTypes = {
  items: PropTypes.array,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  selectAllLabel: PropTypes.string,
  noOptionsText: PropTypes.string,
  limitTags: PropTypes.number,
  onChange: PropTypes.func,
  searchedDcValues: PropTypes.array,
  dcResetKey: PropTypes.number,
  triggerSearch: PropTypes.func,
  onPaste: PropTypes.func
};

export default MultiSelect;
